import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { RealtimeChannel } from '@supabase/supabase-js'

type ContactCampaignHistory = {
  id: string
  account_id: string
  contact_id: string
  campaign_id: string
  message_id: string | null
  campaign_name: string | null
  trigger_type: string | null
  previous_assistant_id: string | null
  new_assistant_id: string | null
  activated_at: string | null
  metadata: Record<string, unknown> | null
}

interface UseContactCampaignsReturn {
  campaignHistory: ContactCampaignHistory[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export const useContactCampaigns = (contactId: string | null): UseContactCampaignsReturn => {
  const { user } = useAuth()
  const [campaignHistory, setCampaignHistory] = useState<ContactCampaignHistory[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const channelRef = useRef<RealtimeChannel | null>(null)

  const fetchCampaignHistory = useCallback(async (): Promise<void> => {
    if (!user?.account_id || !supabase || !contactId) {
      setCampaignHistory([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('account_contact_campaigns')
        .select('*')
        .eq('account_id', user.account_id)
        .eq('contact_id', contactId)
        .order('activated_at', { ascending: false })
        .limit(10)

      if (fetchError) throw fetchError

      setCampaignHistory(data || [])
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar historico de campanhas'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [user?.account_id, contactId])

  useEffect(() => {
    if (!user?.account_id || !supabase || !contactId) {
      setCampaignHistory([])
      return
    }

    fetchCampaignHistory()

    const channel = supabase
      .channel(`contact_campaigns_${contactId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_contact_campaigns',
          filter: `contact_id=eq.${contactId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setCampaignHistory((prev) => {
              const exists = prev.some((c) => c.id === payload.new.id)
              if (exists) return prev
              return [payload.new as ContactCampaignHistory, ...prev].slice(0, 10)
            })
          } else if (payload.eventType === 'DELETE') {
            setCampaignHistory((prev) => prev.filter((c) => c.id !== payload.old.id))
          }
        }
      )
      .subscribe()

    channelRef.current = channel

    return () => {
      if (channelRef.current && supabase) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [user?.account_id, contactId, fetchCampaignHistory])

  const refetch = async (): Promise<void> => {
    await fetchCampaignHistory()
  }

  return {
    campaignHistory,
    loading,
    error,
    refetch,
  }
}
